import { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

type RoleOption = {
    role_id: number;
    role_name: string;
};

type RolePermission = {
    permission_id: number;
    permission_key: string;
    module_code: string;
    entity_code: string;
    action_code: string;
    description: string;
    is_allowed: boolean;
};

type RoleVisibility = {
    visibility_id: number;
    visibility_key: string;
    visibility_scope: string;
    description: string;
    is_visible: boolean;
};

export function RolesPermissionsManager() {
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
    const [permissions, setPermissions] = useState<RolePermission[]>([]);
    const [visibility, setVisibility] = useState<RoleVisibility[]>([]);
    const [newRoleName, setNewRoleName] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingPermissions, setSavingPermissions] = useState(false);
    const [savingVisibility, setSavingVisibility] = useState(false);

    const fetchRoles = async () => {
        const res = await fetch(`${API_BASE_URL}/system/roles`);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to load roles');
        }
        const data = await res.json();
        const next = Array.isArray(data) ? data : [];
        setRoles(next);
        if (next.length > 0 && !selectedRoleId) {
            setSelectedRoleId(next[0].role_id);
        }
    };

    const fetchRolePermissions = async (roleId: number) => {
        const res = await fetch(`${API_BASE_URL}/system/roles/${roleId}/permissions`);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to load role permissions');
        }
        const data = await res.json();
        setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
    };

    const fetchRoleVisibility = async (roleId: number) => {
        const res = await fetch(`${API_BASE_URL}/system/roles/${roleId}/visibility`);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to load role visibility');
        }
        const data = await res.json();
        setVisibility(Array.isArray(data.visibility) ? data.visibility : []);
    };

    useEffect(() => {
        setLoading(true);
        fetchRoles()
            .catch((error: any) => toast.error(error?.message || 'Failed to load roles'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!selectedRoleId) return;
        setLoading(true);
        Promise.all([fetchRolePermissions(selectedRoleId), fetchRoleVisibility(selectedRoleId)])
            .catch((error: any) => toast.error(error?.message || 'Failed to load role access settings'))
            .finally(() => setLoading(false));
    }, [selectedRoleId]);

    const groupedPermissions = useMemo(() => {
        const grouped: Record<string, RolePermission[]> = {};
        for (const permission of permissions) {
            const moduleCode = permission.module_code || 'other';
            if (!grouped[moduleCode]) grouped[moduleCode] = [];
            grouped[moduleCode].push(permission);
        }
        return grouped;
    }, [permissions]);

    const groupedVisibility = useMemo(() => {
        const grouped: Record<string, RoleVisibility[]> = {};
        for (const item of visibility) {
            const scope = item.visibility_scope || 'other';
            if (!grouped[scope]) grouped[scope] = [];
            grouped[scope].push(item);
        }
        return grouped;
    }, [visibility]);

    const togglePermission = (permissionId: number) => {
        setPermissions((prev) => prev.map((item) => (
            item.permission_id === permissionId ? { ...item, is_allowed: !item.is_allowed } : item
        )));
    };

    const toggleVisibility = (visibilityId: number) => {
        setVisibility((prev) => prev.map((item) => (
            item.visibility_id === visibilityId ? { ...item, is_visible: !item.is_visible } : item
        )));
    };

    const savePermissions = async () => {
        if (!selectedRoleId) return;
        setSavingPermissions(true);
        try {
            const payload = permissions.map((item) => ({
                permission_id: item.permission_id,
                is_allowed: !!item.is_allowed,
            }));
            const res = await fetch(`${API_BASE_URL}/system/roles/${selectedRoleId}/permissions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to save permissions');
            }
            toast.success('Permissions saved');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save permissions');
        } finally {
            setSavingPermissions(false);
        }
    };

    const saveVisibility = async () => {
        if (!selectedRoleId) return;
        setSavingVisibility(true);
        try {
            const payload = visibility.map((item) => ({
                visibility_id: item.visibility_id,
                is_visible: !!item.is_visible,
            }));
            const res = await fetch(`${API_BASE_URL}/system/roles/${selectedRoleId}/visibility`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to save visibility');
            }
            toast.success('Visibility saved');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save visibility');
        } finally {
            setSavingVisibility(false);
        }
    };

    const createRole = async () => {
        const name = newRoleName.trim();
        if (!name) return;
        try {
            const res = await fetch(`${API_BASE_URL}/system/roles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role_name: name }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to create role');
            }
            toast.success('Role created');
            setNewRoleName('');
            await fetchRoles();
            if (data?.role_id) {
                setSelectedRoleId(data.role_id);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create role');
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Roles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-3">
                        <Input
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                            placeholder="New role name"
                        />
                        <Button onClick={createRole}>Create Role</Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <label className="text-sm text-slate-600">Selected Role</label>
                            <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={selectedRoleId}
                                onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                            >
                                {roles.map((role) => (
                                    <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                        <CardTitle>Permissions Matrix</CardTitle>
                        <Button onClick={savePermissions} disabled={savingPermissions || !selectedRoleId}>
                            {savingPermissions ? 'Saving...' : 'Save Permissions'}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {loading ? (
                        <div className="text-sm text-slate-500">Loading permissions...</div>
                    ) : permissions.length === 0 ? (
                        <div className="text-sm text-slate-500">No permissions found for this role.</div>
                    ) : (
                        Object.keys(groupedPermissions).sort().map((moduleCode) => (
                            <div key={moduleCode} className="rounded-md border p-3">
                                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-700">
                                    {moduleCode}
                                </h3>
                                <div className="space-y-2">
                                    {groupedPermissions[moduleCode].map((permission) => (
                                        <label
                                            key={permission.permission_id}
                                            className="flex items-center justify-between gap-3 rounded px-2 py-1 hover:bg-slate-50"
                                        >
                                            <span className="text-sm text-slate-700">
                                                {permission.description || permission.permission_key}
                                                <span className="ml-2 text-xs text-slate-500">{permission.permission_key}</span>
                                            </span>
                                            <input
                                                type="checkbox"
                                                checked={!!permission.is_allowed}
                                                onChange={() => togglePermission(permission.permission_id)}
                                                className="h-4 w-4"
                                            />
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-3">
                        <CardTitle>Visibility Matrix</CardTitle>
                        <Button onClick={saveVisibility} disabled={savingVisibility || !selectedRoleId}>
                            {savingVisibility ? 'Saving...' : 'Save Visibility'}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {loading ? (
                        <div className="text-sm text-slate-500">Loading visibility rules...</div>
                    ) : visibility.length === 0 ? (
                        <div className="text-sm text-slate-500">No visibility rules found for this role.</div>
                    ) : (
                        Object.keys(groupedVisibility).sort().map((scope) => (
                            <div key={scope} className="rounded-md border p-3">
                                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-700">
                                    {scope}
                                </h3>
                                <div className="space-y-2">
                                    {groupedVisibility[scope].map((item) => (
                                        <label
                                            key={item.visibility_id}
                                            className="flex items-center justify-between gap-3 rounded px-2 py-1 hover:bg-slate-50"
                                        >
                                            <span className="text-sm text-slate-700">
                                                {item.description || item.visibility_key}
                                                <span className="ml-2 text-xs text-slate-500">{item.visibility_key}</span>
                                            </span>
                                            <input
                                                type="checkbox"
                                                checked={!!item.is_visible}
                                                onChange={() => toggleVisibility(item.visibility_id)}
                                                className="h-4 w-4"
                                            />
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
